import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { combineSx, createStyleSheet, globalStyles } from "../utils/styles";
import { useTranslation } from "react-i18next";

const MAX_REASON_LENGTH = 1000;

const ViewClientPhoneReasonDialog = (props: {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) => {
  const { open, onClose, onSubmit } = props;
  const { t } = useTranslation("ViewClientPhoneReasonDialog");

  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();

  const handleReasonChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setError(undefined);
      setReason(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(() => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setError(t("requiredField"));
      return;
    }

    onSubmit(trimmedReason);
  }, [reason, onSubmit, t]);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(undefined);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("reasonForViewingPhoneNumberTitle")}</DialogTitle>
      <Typography
        variant="body2"
        sx={combineSx(styles.secondaryText, styles.bodyText)}
      >
        {t("bodyText")}
      </Typography>

      <DialogContent>
        <Box sx={styles.dialogContentRow}>
          <TextField
            label={t("reasonFieldLabel")}
            placeholder={t("reasonPlaceholder")}
            fullWidth
            multiline
            minRows={3}
            maxRows={3}
            value={reason}
            onChange={handleReasonChange}
            slotProps={{ htmlInput: { maxLength: MAX_REASON_LENGTH } }}
            error={Boolean(error)}
            helperText={error}
            sx={globalStyles.flexSpace}
            required
          />
        </Box>

        <Typography
          variant="body2"
          sx={combineSx(styles.secondaryText, styles.auditWarningText)}
        >
          {t("auditWarningMessage")}
        </Typography>
      </DialogContent>

      <DialogActions sx={styles.dialogButtonContainer}>
        <Button onClick={onClose}>{t("common:cancel")}</Button>
        <Button onClick={handleSubmit} autoFocus>
          {t("common:submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = createStyleSheet({
  dialogContentRow: {
    display: "flex",
    flexDirection: "row",
    marginTop: 2,
  },
  secondaryText: {
    color: "text.secondary",
  },
  bodyText: {
    mx: 3,
  },
  auditWarningText: {
    marginTop: 1,
  },
  dialogButtonContainer: {
    px: 3,
    marginBottom: 1,
  },
});

export default ViewClientPhoneReasonDialog;
